import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";

const JOURNAL_LABEL = {
  journey90: "Rediscover Yourself · 7-day free trial",
  grief180: "Moving through Grief · $39.99 lifetime",
  sober30: "New Beginnings · Sober Living · $15.99 lifetime",
  chronic365: "Living Well · 7-day free trial",
  bundle: "All Four Journeys Bundle · $109.99 lifetime",
};

const TRIAL_DEST = {
  journey90: "/app",
  chronic365: "/app/chronic",
};

const PAID_ONLY = new Set(["grief180", "sober30", "bundle"]);

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const journal = params.get("journal");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Your journey begins now.");
      if (journal && PAID_ONLY.has(journal)) {
        try {
          const { data } = await api.post("/payments/checkout", {
            origin_url: window.location.origin,
            package_id: journal,
          });
          window.location.href = data.url;
          return;
        } catch (payErr) {
          toast.error(formatApiError(payErr.response?.data?.detail) || "Payment couldn't be started. You can try again from the pricing page.");
          navigate("/app/pricing");
          return;
        }
      }
      if (journal === "chronic365") {
        try { await api.post("/chronic/start-trial"); } catch { /* non-blocking */ }
        navigate("/app/chronic");
        return;
      }
      navigate(TRIAL_DEST[journal] || "/app");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Sign up failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-sand px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-10" data-testid="register-brand">
          <span className="w-2.5 h-2.5 rounded-full bg-moss"></span>
          <span className="font-serif text-xl leading-tight">Rediscover Yourself <span className="text-clay text-sm block">with Angela Gehl</span></span>
        </Link>
        <h1 className="font-serif text-4xl mb-2">Begin your journey.</h1>
        {journal && JOURNAL_LABEL[journal] ? (
          <div className="mb-8 px-4 py-3 rounded-2xl bg-cream border border-border text-sm" data-testid="register-selected-journal">
            <span className="text-muted-foreground">You're starting: </span>
            <span className="font-medium">{JOURNAL_LABEL[journal]}</span>
          </div>
        ) : (
          <p className="text-muted-foreground mb-8">Create your account to begin.</p>
        )}
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-sage font-bold">Your name</label>
            <input required value={name} onChange={(e) => setName(e.target.value)}
                   data-testid="register-name"
                   className="mt-2 w-full px-5 py-3 rounded-2xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-sage"
                   placeholder="What should we call you?" />
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-sage font-bold">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                   data-testid="register-email"
                   className="mt-2 w-full px-5 py-3 rounded-2xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-sage"
                   placeholder="you@example.com" />
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-sage font-bold">Password</label>
            <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                   data-testid="register-password"
                   className="mt-2 w-full px-5 py-3 rounded-2xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-sage"
                   placeholder="At least 6 characters" />
          </div>
          <button type="submit" disabled={loading} data-testid="register-submit"
                  className="w-full py-3.5 rounded-full bg-moss text-white hover:bg-mossDark disabled:opacity-60 transition-all">
            {loading
              ? (journal && PAID_ONLY.has(journal) ? "Creating account…" : "Creating your space…")
              : (journal && PAID_ONLY.has(journal) ? "Create account & continue to checkout" : "Start free trial")}
          </button>
        </form>
        <p className="text-center text-sm text-muted-foreground mt-6">
          Have an account? <Link to={`/login${journal ? `?journal=${journal}` : ""}`} className="text-moss font-medium hover:underline" data-testid="register-to-login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
